/**
 * The notification model — Step 118.
 *
 * Three outbound triggers existed in the whole product, each with its body
 * written as inline HTML inside the route that happened to send it. That is
 * not a small tidiness problem: it means nobody can answer "what do we send,
 * and to whom", there is nothing for an admin to turn off, and every new
 * notification is another hand-written `<p>` in another handler.
 *
 * This is the catalogue and the routing rules. `lib/notify.ts` is the engine
 * that reads them, and Step 96's admin screen edits them.
 *
 * ── Audience is a ROLE, not a list of people ───────────────────────────
 *
 * An event says "the requester" or "whoever it is assigned to", never a set of
 * addresses. The engine resolves that against the ticket at the moment it
 * fires, so reassigning a ticket reroutes its notifications automatically and
 * nobody has to remember to update a list.
 *
 * ── An agent who raises a ticket IS its requester ──────────────────────
 *
 * Worth stating because it looks like a gap: there is no separate "tickets I
 * opened" audience. An agent who raises a ticket is that ticket's requester,
 * so `requester` events — resolved, replied-to — already reach them, and the
 * actor filter stops them being told about their own actions on it.
 *
 * ── Why the ACTOR is always excluded ───────────────────────────────────
 *
 * You do not need telling about the thing you just did. Beyond being noise, a
 * notification that echoes your own action teaches people the notifications
 * are ignorable — which is exactly the habit you cannot afford when a real one
 * arrives. Every audience below is filtered against the actor by the engine.
 */

/** Who an event is for. Roles, resolved per ticket at send time. */
export type Audience =
  | 'requester'
  | 'assignee'
  | 'group'
  | 'watchers'
  | 'mentioned'
  | 'approver'
  /** Everyone on the ticket's Cc list. Addresses, not users — a Cc is somebody
   *  typed in, who may have no account at all. */
  | 'cc';

/**
 * Which tab the settings screen shows it under — Step 96.
 *
 * Mirrors the reference's own grouping (Agent / Requester / CC), because it is
 * the question an admin actually arrives with: "what do my AGENTS get" is a
 * different sitting from "what do customers get". `account` is ours: sign-in
 * mail is not about a ticket and does not belong in either list.
 */
export type NotificationGroup = 'agent' | 'requester' | 'cc' | 'account';

export interface NotificationEvent {
  key: string;
  group: NotificationGroup;
  /** What an admin reads on the settings screen. */
  label: string;
  /** Why it exists, shown under the label — an event whose purpose you cannot
   *  state is one somebody will switch off at random. */
  description: string;
  audiences: Audience[];
  /** Off by default where the volume would outweigh the value. A notification
   *  framework that arrives shouting is one people mute wholesale. */
  defaultOn: boolean;
  /**
   * Shown on the settings screen but not switchable.
   *
   * A magic link IS the way somebody signs in. An admin who turns it off locks
   * every requester out of the portal with no way back, and would have no idea
   * that is what the toggle did. Listing it and disabling the switch is more
   * honest than hiding it and leaving them wondering where it went.
   */
  locked?: boolean;
}

export const NOTIFICATION_EVENTS: NotificationEvent[] = [
  /* ── What AGENTS get ───────────────────────────────────────────────── */
  {
    key: 'ticket.created',
    group: 'agent',
    label: 'New ticket created',
    description: 'New work arrived in a group you belong to. The most useful one for a desk that triages.',
    audiences: ['group'],
    defaultOn: true,
  },
  {
    key: 'ticket.group_assigned',
    group: 'agent',
    label: 'Ticket assigned to your group',
    description: 'A ticket moved into a group you belong to and nobody owns it yet.',
    audiences: ['group'],
    defaultOn: true,
  },
  {
    key: 'ticket.assigned',
    group: 'agent',
    label: 'Ticket assigned to you',
    description: 'It is now yours.',
    audiences: ['assignee'],
    defaultOn: true,
  },
  {
    key: 'requester.replied',
    group: 'agent',
    label: 'Requester replied',
    description: 'The customer has come back. Goes to whoever owns it, or the group if nobody does.',
    audiences: ['assignee', 'group'],
    defaultOn: true,
  },
  {
    key: 'ticket.note_added',
    group: 'agent',
    label: 'Private note added',
    description:
      'Another agent left an internal note on a ticket you own or watch. Never reaches the requester.',
    audiences: ['assignee', 'watchers'],
    defaultOn: true,
  },
  {
    key: 'ticket.note_mentioned',
    group: 'agent',
    label: 'You were tagged in a note',
    description: 'Somebody tagged you specifically. Never reaches the requester — it is an internal note.',
    audiences: ['mentioned'],
    defaultOn: true,
  },
  {
    key: 'approval.requested',
    group: 'agent',
    label: 'Approval needed',
    description: 'Something is waiting on your decision.',
    audiences: ['approver'],
    defaultOn: true,
  },
  {
    key: 'ticket.watched_changed',
    group: 'agent',
    label: 'A ticket you watch changed',
    description: 'For tickets you followed but do not own. Off by default — watchers opt in already.',
    audiences: ['watchers'],
    defaultOn: false,
  },

  /* ── Change and problem (Steps 48/49) ──────────────────────────────────
     Changes and problems are ticket TYPES in the same table, so everything
     above already applies to them. These are the events that only THEY have —
     a change has a CAB decision and a window; a problem has a root cause that
     unblocks every incident linked to it. Without these, the two ITIL
     processes run silently while incidents notify properly. */
  {
    key: 'change.submitted',
    group: 'agent',
    label: 'Change submitted for approval',
    description:
      'Tells the owning group a change has gone to the CAB. The approvers themselves get "Approval needed" instead — never both.',
    /**
     * `approver` was here, and it is why every CAB member got two near-identical
     * emails a second apart — Step 194. This event is the GROUP's awareness that
     * work has left for approval; `approval.requested` is the approver's
     * instruction to decide. Anyone being asked to decide is excluded by the
     * caller, which also covers an approver who sits in the owning group.
     */
    audiences: ['group'],
    defaultOn: true,
  },
  {
    key: 'approval.stood_down',
    group: 'agent',
    label: 'Your approval is no longer needed',
    description:
      'Somebody else decided first, or the request was withdrawn. Closes the loop so a dead request is not left sitting in an inbox.',
    audiences: ['approver'],
    defaultOn: true,
  },
  {
    key: 'change.decided',
    group: 'agent',
    label: 'Change approved or rejected',
    description: 'The decision, to whoever raised it. The one thing a change owner is waiting for.',
    audiences: ['requester', 'assignee'],
    defaultOn: true,
  },
  {
    key: 'change.scheduled',
    group: 'agent',
    label: 'Change window set',
    description: 'The planned start and end changed. Goes to the group doing the work.',
    audiences: ['group', 'assignee'],
    defaultOn: true,
  },
  {
    key: 'problem.linked',
    group: 'agent',
    label: 'Incident linked to a problem',
    description: 'Your incident turned out to be part of something bigger.',
    audiences: ['assignee', 'watchers'],
    defaultOn: true,
  },
  {
    key: 'problem.workaround',
    group: 'agent',
    label: 'Workaround published',
    description:
      'A problem gained a workaround. Reaches everyone holding a linked incident — the point of recording one at all.',
    audiences: ['assignee', 'watchers'],
    defaultOn: true,
  },

  /* ── What REQUESTERS get ───────────────────────────────────────────── */
  {
    key: 'requester.ticket_created',
    group: 'requester',
    label: 'We received your ticket',
    description: 'Acknowledges it and gives them the reference they will quote back.',
    audiences: ['requester'],
    defaultOn: true,
  },
  {
    key: 'agent.replied',
    group: 'requester',
    label: 'An agent replied',
    description: 'The reply itself, sent as the email. Turning this off means agents reply into silence.',
    audiences: ['requester'],
    defaultOn: true,
  },
  {
    key: 'ticket.resolved',
    group: 'requester',
    label: 'Your ticket was resolved',
    description: 'Tells them it is done and what was done.',
    audiences: ['requester'],
    defaultOn: true,
  },
  {
    key: 'ticket.closed',
    group: 'requester',
    label: 'Your ticket was closed',
    description:
      'Separate from resolved on purpose: a desk that auto-closes after a week should be able to send one and not the other.',
    audiences: ['requester'],
    defaultOn: false,
  },
  {
    key: 'ticket.status_changed',
    group: 'requester',
    label: 'Status changed',
    description: 'Every status move. Off by default — on a busy ticket this is most of the noise.',
    audiences: ['requester'],
    defaultOn: false,
  },
  {
    key: 'account.magic_link',
    group: 'account',
    label: 'Portal sign-in link',
    description:
      'How a requester signs in. Cannot be switched off — doing so locks every requester out of the portal with no way back.',
    audiences: ['requester'],
    defaultOn: true,
    locked: true,
  },

  /* ── What people on the Cc get ─────────────────────────────────────── */
  {
    key: 'cc.ticket_created',
    group: 'cc',
    label: 'Ticket they are copied on was raised',
    description: 'For addresses added to a ticket rather than people with accounts.',
    audiences: ['cc'],
    defaultOn: true,
  },
  {
    key: 'cc.replied',
    group: 'cc',
    label: 'A reply on a ticket they are copied on',
    description: 'Keeps a manager or supplier on the thread without giving them an account.',
    audiences: ['cc'],
    defaultOn: true,
  },
];

export const NOTIFICATION_KEYS = NOTIFICATION_EVENTS.map((e) => e.key);

export function notificationEvent(key: string): NotificationEvent | undefined {
  return NOTIFICATION_EVENTS.find((e) => e.key === key);
}

/** The events on one tab of the settings screen, in catalogue order. */
export function eventsInGroup(group: NotificationGroup): NotificationEvent[] {
  return NOTIFICATION_EVENTS.filter((e) => e.group === group);
}

export const NOTIFICATION_GROUPS: { key: NotificationGroup; label: string; blurb: string }[] = [
  { key: 'agent', label: 'Agent notifications', blurb: 'What the service desk hears about.' },
  { key: 'requester', label: 'Requester notifications', blurb: 'What the person who raised it hears about.' },
  {
    key: 'cc',
    label: 'Cc notifications',
    blurb: 'For addresses copied on a ticket, who may have no account.',
  },
  { key: 'account', label: 'Account', blurb: 'Sign-in mail. Not about a ticket.' },
];

/**
 * Is this event on for this org?
 *
 * `overrides` is a sparse patch — the same shape as the vocabulary layer and
 * for the same reason: an org sets the handful it cares about, and an event
 * added later arrives with its own sensible default rather than missing.
 *
 * An unknown key is OFF. A notification we have no catalogue entry for is one
 * nobody chose, nobody can describe on the settings screen, and nobody can
 * switch off — so it does not go out.
 */
export function notificationEnabled(
  key: string,
  overrides: Record<string, boolean> | null | undefined,
): boolean {
  const event = notificationEvent(key);
  if (!event) return false;
  // A locked event ignores overrides entirely — see `locked` for why letting
  // somebody switch off the sign-in link is not a setting, it is a lockout.
  if (event.locked) return true;
  const override = overrides?.[key];
  return typeof override === 'boolean' ? override : event.defaultOn;
}

/**
 * Who should hear about this, given the ticket and who did it.
 *
 * Returns ROLES, not addresses — the engine turns these into people. Empty
 * means nobody: either the event is off, or the only person it would reach is
 * the one who caused it.
 */
export function audiencesFor(key: string, overrides: Record<string, boolean> | null | undefined): Audience[] {
  if (!notificationEnabled(key, overrides)) return [];
  return notificationEvent(key)?.audiences ?? [];
}

/**
 * The subject line, which is the whole notification for most people.
 *
 * Reference FIRST, because it is what threads the reply and what somebody
 * searches their inbox for. Everything after it is context.
 */
export function notificationSubject(ref: string, subject: string, event: string): string {
  const e = notificationEvent(event);
  // The acknowledgement needs no suffix: "We received your ticket" on the mail
  // that acknowledges it is saying the obvious twice.
  const suffix = e && e.key !== 'requester.ticket_created' ? ` — ${e.label}` : '';
  return `[${ref}] ${subject}${suffix}`;
}
